// src/flags.ts
// Flags people can show on their profile (up to 5): pride flags drawn
// here, plus country/region flags from the flag-icons library.
// Stored as ids: "p:<pride id>" or "c:<flag-icons code>".
import countryData from "flag-icons/country.json";

export const MAX_FLAGS = 5;

export type FlagGroup = "pride" | "indigenous" | "community" | "heritage";

export type PrideFlag = {
  id: string;
  name: string;
  stripes: string[];
  vertical?: boolean;
  group?: FlagGroup; // defaults to "pride"
  alsoIn?: FlagGroup[]; // also listed under these picker tabs
  extra?:
    | "progress"
    | "intersexProgress"
    | "demi"
    | "intersex"
    | "twoSpirit"
    | "disability"
    | "violet"
    | "pi"
    | "infinityRainbow"
    | "infinityGold"
    | "deaf"
    | "aboriginal"
    | "torresStrait"
    | "sami"
    | "romani"
    | "kurdistan"
    | "metis"
    | "haudenosaunee"
    | "mikmaq"
    | "wiphala"
    | "tinoRangatiratanga"
    | "juneteenth"
    | "amazigh"
    | "assyrian"
    | "druze"
    | "uyghur"
    | "tibet"
    | "circassian"
    | "acadian"
    | "cornish"
    | "breton"
    | "bearPaw"
    | "ribbonRed"
    | "ribbonGreen"
    | "spoon"
    | "jewishPride"
    | "leatherHeart";
};

// Country flags that also show in a picker tab, with a friendlier label there.
// `after` places one right after that pride flag (otherwise it goes at the end).
export const GROUP_COUNTRIES: Partial<
  Record<FlagGroup, { code: string; label: string; after?: string }[]>
> = {
  heritage: [{ code: "ps", label: "Palestine", after: "circassian" }],
};

export const groupOf = (f: PrideFlag): FlagGroup => f.group || "pride";

export const inGroup = (f: PrideFlag, g: FlagGroup) =>
  groupOf(f) === g || !!f.alsoIn?.includes(g);

const RAINBOW = ["#e40303", "#ff8c00", "#ffed00", "#008026", "#24408e", "#732982"];

export const PRIDE_FLAGS: PrideFlag[] = [
  { id: "rainbow", name: "Rainbow Pride", stripes: RAINBOW },
  { id: "progress", name: "Progress Pride", stripes: RAINBOW, extra: "progress" },
  // Gilbert Baker's first pride flag (1978), with hot pink and turquoise
  {
    id: "rainbow1978",
    name: "Original Rainbow (1978)",
    stripes: ["#ff69b4", "#ff0000", "#ff8e00", "#ffff00", "#008e00", "#00c0c0", "#400098", "#8e008e"],
  },
  { id: "trans", name: "Transgender", stripes: ["#5bcefa", "#f5a9b8", "#ffffff", "#f5a9b8", "#5bcefa"] },
  { id: "nonbinary", name: "Nonbinary", stripes: ["#fcf434", "#ffffff", "#9c59d1", "#2c2c2c"] },
  { id: "genderqueer", name: "Genderqueer", stripes: ["#b57edc", "#ffffff", "#4a8123"] },
  {
    id: "genderfluid",
    name: "Genderfluid",
    stripes: ["#ff76a4", "#ffffff", "#c011d7", "#000000", "#2f3cbe"],
  },
  {
    id: "agender",
    name: "Agender",
    stripes: ["#000000", "#bcc4c7", "#ffffff", "#b7f684", "#ffffff", "#bcc4c7", "#000000"],
  },
  {
    id: "demigirl",
    name: "Demigirl",
    stripes: ["#7f7f7f", "#c4c4c4", "#fdadc8", "#ffffff", "#fdadc8", "#c4c4c4", "#7f7f7f"],
  },
  {
    id: "demiboy",
    name: "Demiboy",
    stripes: ["#7f7f7f", "#c4c4c4", "#9ad9eb", "#ffffff", "#9ad9eb", "#c4c4c4", "#7f7f7f"],
  },
  { id: "intersex", name: "Intersex", stripes: ["#ffd800"], extra: "intersex" },
  {
    id: "twoSpirit",
    name: "Two-Spirit",
    stripes: RAINBOW,
    extra: "twoSpirit",
    alsoIn: ["indigenous"],
  },
  { id: "lesbian", name: "Lesbian", stripes: ["#d52d00", "#ff9a56", "#ffffff", "#d362a4", "#a30262"] },
  {
    id: "gay",
    name: "Gay men (MLM)",
    stripes: ["#078d70", "#26ceaa", "#98e8c1", "#ffffff", "#7bade2", "#5049cc", "#3d1a78"],
  },
  { id: "bi", name: "Bisexual", stripes: ["#d60270", "#d60270", "#9b4f96", "#0038a8", "#0038a8"] },
  { id: "pan", name: "Pansexual", stripes: ["#ff218c", "#ffd800", "#21b1ff"] },
  { id: "ace", name: "Asexual", stripes: ["#000000", "#a3a3a3", "#ffffff", "#800080"] },
  {
    id: "demisexual",
    name: "Demisexual",
    stripes: ["#ffffff", "#ffffff", "#6e0070", "#d2d2d2", "#d2d2d2"],
    extra: "demi",
  },
  { id: "aro", name: "Aromantic", stripes: ["#3da542", "#a7d379", "#ffffff", "#a9a9a9", "#000000"] },
  { id: "aroace", name: "Aroace", stripes: ["#e28c00", "#eccd00", "#ffffff", "#62aedc", "#203856"] },
  // ---- more pride and identity flags ----
  {
    id: "intersexProgress",
    name: "Intersex-Inclusive Progress",
    stripes: RAINBOW,
    extra: "intersexProgress",
  },
  {
    id: "philly",
    name: "Philadelphia Pride",
    stripes: ["#000000", "#784f17", ...RAINBOW],
  },
  {
    id: "transfem",
    name: "Transfeminine",
    stripes: ["#73deff", "#ffe2ee", "#ffb5d6", "#fe76a2", "#ffb5d6", "#ffe2ee", "#73deff"],
  },
  {
    id: "transmasc",
    name: "Transmasculine",
    stripes: ["#ff8abd", "#cdf5fe", "#9aebff", "#74dfff", "#9aebff", "#cdf5fe", "#ff8abd"],
  },
  {
    id: "bigender",
    name: "Bigender",
    stripes: ["#c479a2", "#eda5cd", "#d6c7e8", "#ffffff", "#d6c7e8", "#9ac7e8", "#6d82d1"],
  },
  {
    id: "pangender",
    name: "Pangender",
    stripes: ["#fff798", "#feddcd", "#ffebfb", "#ffffff", "#ffebfb", "#feddcd", "#fff798"],
  },
  { id: "androgyne", name: "Androgyne", stripes: ["#fe007f", "#9832ff", "#00b8e7"], vertical: true },
  {
    id: "sapphic",
    name: "Sapphic",
    stripes: ["#fd8ba8", "#ffffff", "#fd8ba8"],
    extra: "violet",
  },
  { id: "polysexual", name: "Polysexual", stripes: ["#f714ba", "#01d66a", "#1594f6"] },
  {
    id: "omnisexual",
    name: "Omnisexual",
    stripes: ["#ff9ccd", "#ff53bf", "#270067", "#6760fe", "#8ea6ff"],
  },
  {
    id: "greyace",
    name: "Greysexual (grey-ace)",
    stripes: ["#740194", "#aeb1aa", "#ffffff", "#aeb1aa", "#740194"],
  },
  {
    id: "demiromantic",
    name: "Demiromantic",
    stripes: ["#ffffff", "#ffffff", "#3da542", "#d2d2d2", "#d2d2d2"],
    extra: "demi",
  },
  { id: "polyamory", name: "Polyamory", stripes: ["#0000ff", "#ff0000", "#000000"], extra: "pi" },
  {
    id: "bear",
    name: "Bear",
    stripes: ["#623804", "#d56300", "#fedd63", "#fee6b8", "#ffffff", "#555555", "#000000"],
    extra: "bearPaw",
  },
  {
    id: "leather",
    name: "Leather Pride",
    stripes: ["#000000", "#0021a5", "#000000", "#0021a5", "#ffffff", "#0021a5", "#000000", "#0021a5", "#000000"],
    extra: "leatherHeart",
  },
  { id: "queer", name: "Queer", stripes: ["#000000", "#9ad9ea", "#00a3e8", "#b5e51d", "#ffffff", "#ffaec9", "#fc5c7d"] },
  { id: "neutrois", name: "Neutrois", stripes: ["#ffffff", "#2f9c4a", "#000000"] },
  { id: "genderflux", name: "Genderflux", stripes: ["#f47694", "#f2a2b9", "#cecece", "#7ce0f7", "#3ecdf9", "#fff48d"] },
  { id: "polygender", name: "Polygender", stripes: ["#939393", "#ed94c5", "#f5ed00", "#64bbe6", "#2676d2"] },
  { id: "abrosexual", name: "Abrosexual", stripes: ["#75ca91", "#b3e4c7", "#ffffff", "#e695b5", "#d9446d"] },
  { id: "aceflux", name: "Aceflux", stripes: ["#ff6d9c", "#ffadc8", "#ffffff", "#a6d6f2", "#4e9fd6"] },

  // ---- disability and neurodivergent community ----
  {
    id: "disability",
    name: "Disability Pride",
    stripes: ["#595959"],
    group: "community",
    extra: "disability",
  },
  {
    id: "neurodivergent",
    name: "Neurodivergent Pride",
    stripes: ["#ffffff"],
    group: "community",
    extra: "infinityRainbow",
  },
  {
    id: "autistic",
    name: "Autistic Pride",
    stripes: ["#ffffff"],
    group: "community",
    extra: "infinityGold",
  },
  { id: "deaf", name: "Deaf Pride", stripes: ["#2aa6c9"], group: "community", extra: "deaf" },
  // Symbols rather than flags: these communities use a ribbon or a spoon.
  {
    id: "hivRibbon",
    name: "HIV/AIDS awareness (red ribbon)",
    stripes: ["#ffffff"],
    group: "community",
    extra: "ribbonRed",
  },
  {
    id: "spoonie",
    name: "Chronic illness (spoonie)",
    stripes: ["#d9ccf0"],
    group: "community",
    extra: "spoon",
  },
  {
    id: "mentalHealth",
    name: "Mental health (green ribbon)",
    stripes: ["#ffffff"],
    group: "community",
    extra: "ribbonGreen",
  },

  // ---- Indigenous flags (Two-Spirit shows here too) ----
  { id: "metis", name: "Métis", stripes: ["#2233cc"], group: "indigenous", extra: "metis" },
  {
    id: "haudenosaunee",
    name: "Haudenosaunee",
    stripes: ["#593eb6"],
    group: "indigenous",
    extra: "haudenosaunee",
  },
  { id: "mikmaq", name: "Mi'kmaq", stripes: ["#ffffff"], group: "indigenous", extra: "mikmaq" },
  {
    id: "wiphala",
    name: "Wiphala (Andean)",
    stripes: ["#ffffff"],
    group: "indigenous",
    extra: "wiphala",
  },
  {
    id: "tinoRangatiratanga",
    name: "Tino Rangatiratanga (Māori)",
    stripes: ["#ffffff"],
    group: "indigenous",
    extra: "tinoRangatiratanga",
  },
  {
    id: "aboriginal",
    name: "Aboriginal Australian",
    stripes: ["#000000", "#cc0000"],
    group: "indigenous",
    extra: "aboriginal",
  },
  {
    id: "torresStrait",
    name: "Torres Strait Islander",
    stripes: ["#2f9e44"],
    group: "indigenous",
    extra: "torresStrait",
  },
  { id: "sami", name: "Sámi", stripes: ["#d52b1e"], group: "indigenous", extra: "sami" },

  // ---- heritage flags ----
  { id: "panAfrican", name: "Pan-African", stripes: ["#e31b23", "#000000", "#00853f"], group: "heritage" },
  { id: "juneteenth", name: "Juneteenth", stripes: ["#0a3d91"], group: "heritage", extra: "juneteenth" },
  { id: "garifuna", name: "Garifuna", stripes: ["#000000", "#ffffff", "#ffd700"], group: "heritage" },
  // the rainbow with a Star of David: being Jewish and queer (not a country's flag)
  { id: "jewishPride", name: "Jewish Pride", stripes: RAINBOW, group: "heritage", extra: "jewishPride" },
  {
    id: "amazigh",
    name: "Amazigh (Berber)",
    stripes: ["#0090da", "#78be20", "#fedd00"],
    group: "heritage",
    extra: "amazigh",
  },
  { id: "assyrian", name: "Assyrian", stripes: ["#ffffff"], group: "heritage", extra: "assyrian" },
  {
    id: "druze",
    name: "Druze",
    stripes: ["#d2162d", "#ffd706", "#0038b8", "#ffffff"],
    group: "heritage",
    extra: "druze",
  },
  { id: "uyghur", name: "Uyghur (East Turkestan)", stripes: ["#559ee2"], group: "heritage", extra: "uyghur" },
  { id: "tibet", name: "Tibet", stripes: ["#ffffff"], group: "heritage", extra: "tibet" },
  { id: "circassian", name: "Circassian", stripes: ["#00843d"], group: "heritage", extra: "circassian" },
  { id: "cornish", name: "Cornish", stripes: ["#000000"], group: "heritage", extra: "cornish" },
  {
    id: "breton",
    name: "Breton",
    stripes: ["#000000", "#ffffff", "#000000", "#ffffff", "#000000", "#ffffff", "#000000", "#ffffff", "#000000"],
    group: "heritage",
    extra: "breton",
  },
  { id: "romani", name: "Romani", stripes: ["#0d4ea8", "#3f9a3a"], group: "heritage", extra: "romani" },
  {
    id: "kurdistan",
    name: "Kurdistan",
    stripes: ["#ed2024", "#ffffff", "#278e43"],
    group: "heritage",
    extra: "kurdistan",
  },
  {
    id: "acadian",
    name: "Acadian",
    stripes: ["#003896", "#ffffff", "#cf142b"],
    vertical: true,
    group: "heritage",
    extra: "acadian",
  },
];

export type Country = { code: string; name: string };

export const COUNTRIES: Country[] = (countryData as { code: string; name: string }[])
  .map((c) => ({ code: c.code, name: c.name }))
  .sort((a, b) => a.name.localeCompare(b.name));

export type FlagInfo =
  | { kind: "pride"; id: string; name: string; flag: PrideFlag }
  | { kind: "country"; id: string; name: string; code: string };

export function flagInfo(id: string): FlagInfo | null {
  if (id.startsWith("p:")) {
    const flag = PRIDE_FLAGS.find((f) => f.id === id.slice(2));
    return flag ? { kind: "pride", id, name: flag.name, flag } : null;
  }
  if (id.startsWith("c:")) {
    const c = COUNTRIES.find((x) => x.code === id.slice(2));
    return c ? { kind: "country", id, name: c.name, code: c.code } : null;
  }
  return null;
}

// Keep only known flags, no duplicates, at most 5.
export function normalizeFlags(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  return raw
    .filter((id): id is string => typeof id === "string" && !!flagInfo(id))
    .filter((id) => (seen.has(id) ? false : (seen.add(id), true)))
    .slice(0, MAX_FLAGS);
}
